import { useState } from 'react';
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	List,
	ListItem,
	ListItemSecondaryAction,
	ListItemText,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import { VariantInterface } from '../types';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import apiClient from '../services/apiClient';
import notify from '../services/notify';
import { getCurrency } from '../services/tableHelper';
import { useSelector } from 'react-redux';
import ConfirmationModal from './ConfirmationModal';

interface VariantEditorProps {
	productId?: string;
	variants: VariantInterface[];
	onChange: (variants: VariantInterface[]) => void;
}

interface VariantFormData {
	name: string;
	description: string;
	default_price: string;
	min_stock_level: string;
	is_visible_in_storefront: boolean;
}

const VariantEditor: React.FC<VariantEditorProps> = ({ productId, variants, onChange }) => {
	const [dialogOpen, setDialogOpen] = useState<boolean>(false);
	const [editingVariant, setEditingVariant] = useState<VariantInterface | null>(null);
	const [formData, setFormData] = useState<VariantFormData>({
		name: '',
		description: '',
		default_price: '0',
		min_stock_level: '0',
		is_visible_in_storefront: true,
	});
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
	const [variantToDelete, setVariantToDelete] = useState<string | null>(null);

	const currency = useSelector((state: any) => getCurrency(state.auth.user.currency));

	const openAddDialog = () => {
		setEditingVariant(null);
		setFormData({
			name: '',
			description: '',
			default_price: '0',
			min_stock_level: '0',
			is_visible_in_storefront: true,
		});
		setDialogOpen(true);
	};

	const openEditDialog = (variant: VariantInterface) => {
		setEditingVariant(variant);
		setFormData({
			name: variant.name,
			description: variant.description || '',
			default_price: variant.default_price.toString(),
			min_stock_level: variant.min_stock_level?.toString() || '0',
			is_visible_in_storefront: Boolean(variant.is_visible_in_storefront),
		});
		setDialogOpen(true);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value, type, checked } = e.target;

		if (type === 'checkbox') {
			setFormData((prev) => ({ ...prev, [name]: checked }));
		} else {
			setFormData((prev) => ({ ...prev, [name]: value }));
		}
	};

	const validateForm = (): boolean => {
		if (!formData.name.trim()) {
			notify('error', 'Variant name is required');
			return false;
		}

		if (isNaN(Number(formData.default_price)) || Number(formData.default_price) <= 0) {
			notify('error', 'Price must be a valid number greater than 0');
			return false;
		}
		if (isNaN(Number(formData.min_stock_level)) || Number(formData.min_stock_level) < 0) {
			notify('error', 'Minimum stock level must be a valid number greater than or equal to 0');
			return false;
		}

		return true;
	};

	const handleSubmit = async () => {
		if (!validateForm()) return;
		if (!productId) {
			notify('error', 'Please save the product first before adding variants');
			return;
		}

		setIsSubmitting(true);

		try {
			const variantData = {
				name: formData.name.trim(),
				description: formData.description.trim() || undefined,
				default_price: Number(formData.default_price),
				min_stock_level: Number(formData.min_stock_level),
				product_id: Number(productId),
				is_visible_in_storefront: formData.is_visible_in_storefront,
			};

			if (editingVariant) {
				// Update existing variant
				const result = await apiClient.update(`/variants/${editingVariant.id}`, variantData);

				// Update local state
				const updatedVariants = variants.map((v) =>
					v.id === editingVariant.id ? { ...v, ...(result.data as VariantInterface) } : v
				);
				onChange(updatedVariants);
				notify('success', 'Variant updated successfully');
			} else {
				// Create new variant
				const result = await apiClient.post('/variants', variantData);

				// Add to local state
				onChange([...variants, result.data as VariantInterface]);
				notify('success', 'Variant added successfully');
			}

			setDialogOpen(false);
		} catch (error) {
			console.error('Error saving variant:', error);
			notify('error', 'Failed to save variant');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDeleteClick = (variantId: string) => {
		setVariantToDelete(variantId);
		setDeleteConfirmOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!productId || !variantToDelete) return;

		try {
			await apiClient.delete(`/variants/${variantToDelete}`);

			// Update local state by filtering out the deleted variant
			const updatedVariants = variants.filter((v) => String(v.id) !== String(variantToDelete));
			onChange(updatedVariants);

			notify('success', 'Variant deleted successfully');
		} catch (error) {
			console.error('Error deleting variant:', error);
			notify('error', 'Failed to delete variant');
		} finally {
			setDeleteConfirmOpen(false);
			setVariantToDelete(null);
		}
	};

	const handleDeleteCancel = () => {
		setDeleteConfirmOpen(false);
		setVariantToDelete(null);
	};

	return (
		<Box sx={{ mt: 2 }}>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
				<Typography variant="subtitle1">Variants</Typography>
				<Button
					variant="outlined"
					size="small"
					startIcon={<AddIcon />}
					onClick={openAddDialog}
					disabled={!productId}
				>
					Add Variant
				</Button>
			</Box>

			{!productId && (
				<Typography variant="caption" color="text.secondary">
					Save the product first to add variants
				</Typography>
			)}

			{variants.length > 0 ? (
				<List dense sx={{ bgcolor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: 1 }}>
					{variants.map((variant) => (
						<ListItem key={variant.id}>
							<ListItemText
								primary={
									<Box sx={{ display: 'flex', alignItems: 'center' }}>
										{variant.name}
										{/* {variant.is_default_variant && (
											<Chip
												label="Default"
												size="small"
												color="primary"
												variant="outlined"
												sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
											/>
										)} */}
										{variant.is_visible_in_storefront === false && (
											<Chip
												label="Hidden"
												size="small"
												color="default"
												variant="outlined"
												sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
											/>
										)}
									</Box>
								}
								secondary={`Price: ${variant.default_price}`}
							/>
							<ListItemSecondaryAction>
								<IconButton edge="end" onClick={() => openEditDialog(variant)}>
									<EditIcon fontSize="small" />
								</IconButton>
								<IconButton edge="end" onClick={() => handleDeleteClick(String(variant.id))}>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</ListItemSecondaryAction>
						</ListItem>
					))}
				</List>
			) : (
				<Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
					<Typography variant="body2" color="text.secondary">
						No variants added yet
					</Typography>
				</Box>
			)}

			{/* Variant Add/Edit Dialog */}
			<Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>{editingVariant ? 'Edit Variant' : 'Add Variant'}</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						margin="dense"
						name="name"
						label="Variant Name"
						type="text"
						fullWidth
						variant="outlined"
						value={formData.name}
						onChange={handleInputChange}
						sx={{ mb: 2 }}
					/>
					<TextField
						margin="dense"
						name="description"
						label="Description (Optional)"
						type="text"
						fullWidth
						variant="outlined"
						value={formData.description}
						onChange={handleInputChange}
						sx={{ mb: 2 }}
						multiline
						rows={2}
					/>
					<TextField
						margin="dense"
						name="min_stock_level"
						label="Mininum Stock Level"
						type="number"
						fullWidth
						variant="outlined"
						value={formData.min_stock_level}
						onChange={handleInputChange}
						sx={{ mb: 2 }}
					/>
					<TextField
						margin="dense"
						name="default_price"
						label={`Price (${currency})`}
						type="number"
						fullWidth
						variant="outlined"
						value={formData.default_price}
						onChange={handleInputChange}
						sx={{ mb: 2 }}
					/>

					<FormControlLabel
						control={
							<Switch
								checked={formData.is_visible_in_storefront}
								onChange={handleInputChange}
								name="is_visible_in_storefront"
								color="primary"
							/>
						}
						label="Visible in storefront"
					/>
					<Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2, mb: 2 }}>
						{formData.is_visible_in_storefront
							? 'This variant will be displayed in your storefront.'
							: 'This variant will be hidden from your storefront.'}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDialogOpen(false)}>Cancel</Button>
					<Button onClick={handleSubmit} variant="contained" disabled={isSubmitting}>
						{isSubmitting ? 'Saving...' : 'Save'}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Delete Confirmation Modal */}
			<ConfirmationModal
				isOpen={deleteConfirmOpen}
				title="Delete Variant"
				message="Are you sure you want to delete this variant? This action cannot be undone."
				onConfirm={handleDeleteConfirm}
				onCancel={handleDeleteCancel}
				confirmText="Delete"
				cancelText="Cancel"
				variant="danger"
			/>
		</Box>
	);
};

export default VariantEditor;
